/**
 * Mode-aware service layer for ServiceRequest + EpisodeOfCare.
 *
 * - **Mock mode** delegates to the existing `mockLifecycleData` so the tracker UI
 *   keeps rendering without a backend. No mutations are made in mock mode.
 * - **Live mode** reads/writes Medplum FHIR resources according to
 *   `docs/PRD_SERVICE_REQUESTS_FHIR_MAPPING.md`.
 *
 * Callers pass a `MedplumClient` (from `useMedplum`) and the `live` flag so this
 * module has no direct dependency on the mock toggle.
 */

import type { MedplumClient } from '@medplum/core';
import type {
  Annotation,
  Appointment,
  Coverage,
  CoverageEligibilityResponse,
  EpisodeOfCare,
  Patient,
  Practitioner,
  QuestionnaireResponse,
  Resource,
  ServiceRequest,
} from '@medplum/fhirtypes';
import {
  APPOINTMENT_SERVICE_TYPE_SYSTEM,
  SCREENING_CALL_SERVICE_TYPE_CODE,
} from '@unify/fhir-model/constants';
import {
  RBAC_PILOT_TAG_CODE,
  RBAC_PILOT_TAG_SYSTEM,
} from './caseloadFhirConstants';
import { assertNoOpenEpisodeOfType } from './openEpisodeOfCare';
import {
  SERVICE_REQUEST_CATEGORY,
  SERVICE_REQUEST_STATIC_INTENT,
  SERVICE_REQUEST_STATIC_STATUS,
  TRACKER_STAGE_TAG_SYSTEM,
  buildEpisodeOfCareExtension,
  buildTrackerStageTag,
  hasServiceRequestCategory,
  readEpisodeOfCareId,
  readTrackerStage,
  therapyServiceTypeConcept,
  trackerStageSearchToken,
  upsertTrackerStageTag,
  type TrackerStageCode,
} from './serviceRequestFhirConstants';
import {
  applyStageToStatus,
  buildTrackerRowLookup,
  serviceRequestToTrackerRow,
  type BuildEpisodeOfCareInput,
  type BuildServiceRequestInput,
  type EpisodeOfCareCareManagerReference,
  type EpisodeOfCareManagingOrganizationReference,
  type ServiceRequestRequesterReference,
  type TrackerRowLookup,
} from './serviceRequestFhirMapping';
import type { EpisodeRow } from '../pages/episode-tracker/types/lifecycle';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ServiceRequestPair {
  serviceRequest: ServiceRequest;
  episodeOfCare: EpisodeOfCare;
}

export interface TrackerRowBundle {
  rows: EpisodeRow[];
  /** Raw resources so downstream UI can reach back for details without re-fetching. */
  serviceRequests: ServiceRequest[];
  lookup: TrackerRowLookup;
}

export interface CreateServiceRequestInput {
  patient: Patient;
  /** Therapy service type code for `ServiceRequest.code`. */
  therapyTypeCode: string;
  /** Stage to record in `meta.tag`. Defaults to `new-client`. */
  stage?: TrackerStageCode;
  /**
   * Who originated the request. Maps to `ServiceRequest.requester`.
   * - Client intake / portal: the patient
   * - Provider on behalf of client: Practitioner
   * - Admin team: Organization or Practitioner
   */
  requester?: ServiceRequestRequesterReference;
  /**
   * Care manager for the paired EpisodeOfCare. Required when the stage resolves to
   * an `active` episode (`fulfilled`) — the UnifyEpisodeOfCare profile rejects an
   * active episode without one, so it must be set on the initial create.
   */
  careManager?: EpisodeOfCareCareManagerReference;
  managingOrganization?: EpisodeOfCareManagingOrganizationReference;
  /** Practice Organization on ServiceRequest.performer (preferred over EoC.managingOrganization). */
  performerOrganization?: EpisodeOfCareManagingOrganizationReference;
  /** Optional initial free-text note (e.g. client's reason for requesting service). */
  initialNote?: string;
  /** Optional ISO timestamp override. */
  authoredOn?: string;
  /** Additional meta tags beyond the tracker stage + pilot seed tag. */
  additionalTags?: { system: string; code: string; display?: string }[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function nowIso(): string {
  return new Date().toISOString();
}

/** True when an Appointment is a high-risk screening call (by service-type coding). */
function isScreeningCallAppointment(appt: Appointment): boolean {
  return Boolean(
    appt.serviceType?.some((st) =>
      st.coding?.some(
        (c) => c.system === APPOINTMENT_SERVICE_TYPE_SYSTEM && c.code === SCREENING_CALL_SERVICE_TYPE_CODE,
      ),
    ),
  );
}

/** Primary-performer (PPRF) practitioner id on an Appointment, falling back to any practitioner. */
function appointmentPractitionerId(appt: Appointment): string | undefined {
  const isPractitioner = (ref?: string): boolean => Boolean(ref?.startsWith('Practitioner/'));
  const pprf = appt.participant?.find(
    (p) =>
      isPractitioner(p.actor?.reference) &&
      p.type?.some((t) => t.coding?.some((c) => c.code === 'PPRF')),
  );
  const ref =
    pprf?.actor?.reference ??
    appt.participant?.find((p) => isPractitioner(p.actor?.reference))?.actor?.reference;
  return ref?.startsWith('Practitioner/') ? ref.slice('Practitioner/'.length) : undefined;
}

function practitionerFullName(p?: Practitioner): string | undefined {
  const n = p?.name?.[0];
  if (!n) return p?.id;
  const full = [(n.prefix ?? []).join(' '), (n.given ?? []).join(' '), n.family]
    .filter(Boolean)
    .join(' ')
    .trim();
  return full || p?.id;
}

function withPilotTag<T extends Resource>(resource: T): T {
  const existing = resource.meta?.tag ?? [];
  const hasPilot = existing.some(
    (t) => t.system === RBAC_PILOT_TAG_SYSTEM && t.code === RBAC_PILOT_TAG_CODE,
  );
  if (hasPilot) return resource;
  return {
    ...resource,
    meta: {
      ...(resource.meta ?? {}),
      tag: [...existing, { system: RBAC_PILOT_TAG_SYSTEM, code: RBAC_PILOT_TAG_CODE }],
    },
  };
}

function addTagsToResource<T extends Resource>(
  resource: T,
  tags?: { system: string; code: string; display?: string }[],
): T {
  if (!tags || tags.length === 0) return resource;
  const existing = resource.meta?.tag ?? [];
  const next = [...existing];
  for (const t of tags) {
    const present = next.some((e) => e.system === t.system && e.code === t.code);
    if (!present) next.push(t);
  }
  return { ...resource, meta: { ...(resource.meta ?? {}), tag: next } };
}

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

export function buildServiceRequestResource(
  input: BuildServiceRequestInput,
): ServiceRequest {
  const sr: ServiceRequest = {
    resourceType: 'ServiceRequest',
    status: SERVICE_REQUEST_STATIC_STATUS,
    intent: SERVICE_REQUEST_STATIC_INTENT,
    priority: input.priority ?? 'routine',
    meta: {
      tag: [buildTrackerStageTag(input.stage)],
    },
    category: [SERVICE_REQUEST_CATEGORY],
    code: therapyServiceTypeConcept(input.therapyTypeCode),
    subject: {
      reference: `Patient/${input.patient.id}`,
      display: patientDisplay(input.patient),
    },
    requester: input.requester,
    authoredOn: input.authoredOn ?? nowIso(),
    ...(input.performerOrganization ? { performer: [input.performerOrganization] } : {}),
  };
  if (input.initialNote) {
    const note: Annotation = {
      text: input.initialNote,
      time: sr.authoredOn,
    };
    sr.note = [note];
  }
  if (input.episodeOfCareId) {
    sr.extension = [
      buildEpisodeOfCareExtension(input.episodeOfCareId, episodeOfCareDisplay(input)),
    ];
  }
  return addTagsToResource(sr, input.additionalTags);
}

export function buildEpisodeOfCareResource(
  input: BuildEpisodeOfCareInput,
): EpisodeOfCare {
  const eoc: EpisodeOfCare = {
    resourceType: 'EpisodeOfCare',
    status: input.status ?? 'planned',
    patient: {
      reference: `Patient/${input.patient.id}`,
      display: patientDisplay(input.patient),
    },
    type: [therapyServiceTypeConcept(input.therapyTypeCode)],
    ...(input.careManagerReference ? { careManager: input.careManagerReference } : {}),
    ...(input.managingOrganizationReference
      ? { managingOrganization: input.managingOrganizationReference }
      : {}),
    period: {
      start: input.periodStart ?? nowIso(),
    },
  };
  return addTagsToResource(eoc, input.additionalTags);
}

function patientDisplay(p: Patient): string | undefined {
  const n = p.name?.[0];
  if (!n) return undefined;
  const given = (n.given ?? []).join(' ');
  const full = [given, n.family].filter(Boolean).join(' ').trim();
  return full || undefined;
}

function episodeOfCareDisplay(input: BuildServiceRequestInput): string | undefined {
  const name = patientDisplay(input.patient);
  if (!name) return undefined;
  return `${capitalize(input.therapyTypeCode)} — ${name}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
}

// ---------------------------------------------------------------------------
// Create (ServiceRequest + paired EpisodeOfCare)
// ---------------------------------------------------------------------------

/**
 * Create a ServiceRequest + paired EpisodeOfCare. Returns both resources with
 * server-assigned ids, including the SR with the `episode-of-care` extension set.
 */
export async function createServiceRequestPair(opts: {
  live: boolean;
  medplum: MedplumClient;
  input: CreateServiceRequestInput;
}): Promise<ServiceRequestPair | null> {
  if (!opts.live) return null;

  const { medplum, input } = opts;
  const stage = input.stage ?? 'new-client';
  const { episodeOfCareStatus } = applyStageToStatus(stage);

  if (episodeOfCareStatus === 'active' && !input.careManager) {
    throw new Error(
      `Cannot create an active EpisodeOfCare for stage "${stage}" without a careManager.`,
    );
  }

  // One open episode per therapy type per patient (group therapy excepted).
  if (input.patient.id) {
    await assertNoOpenEpisodeOfType(medplum, input.patient.id, input.therapyTypeCode);
  }

  const eocDraft = buildEpisodeOfCareResource({
    patient: input.patient,
    therapyTypeCode: input.therapyTypeCode,
    status: episodeOfCareStatus,
    careManagerReference: input.careManager,
    managingOrganizationReference: input.managingOrganization,
  });
  const eoc = await medplum.createResource<EpisodeOfCare>(withPilotTag(eocDraft));

  const requester: ServiceRequestRequesterReference =
    input.requester ??
    {
      reference: `Patient/${input.patient.id}`,
      display: patientDisplay(input.patient),
    };

  const srDraft = buildServiceRequestResource({
    patient: input.patient,
    therapyTypeCode: input.therapyTypeCode,
    stage,
    requester,
    episodeOfCareId: eoc.id,
    initialNote: input.initialNote,
    authoredOn: input.authoredOn,
    additionalTags: input.additionalTags,
    performerOrganization: input.performerOrganization,
  });
  const sr = await medplum.createResource<ServiceRequest>(withPilotTag(srDraft));

  return { serviceRequest: sr, episodeOfCare: eoc };
}

// ---------------------------------------------------------------------------
// Read (stage queries for the tracker)
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 100;

export async function listServiceRequestsByStage(opts: {
  live: boolean;
  medplum: MedplumClient;
  stage?: TrackerStageCode;
  count?: number;
}): Promise<ServiceRequest[]> {
  if (!opts.live) return [];
  const params: Record<string, string> = {
    _count: String(opts.count ?? DEFAULT_PAGE_SIZE),
    _sort: '-_lastUpdated',
  };
  if (opts.stage) {
    params._tag = trackerStageSearchToken(opts.stage);
  }
  const results = await opts.medplum.searchResources('ServiceRequest', params);
  return results.filter(
    (sr) => hasServiceRequestCategory(sr) && readTrackerStage(sr.meta) !== undefined,
  );
}

/** Fetch SR + all resources the tracker rows reference, then map to rows. */
export async function listTrackerRows(opts: {
  live: boolean;
  medplum: MedplumClient;
  stage?: TrackerStageCode;
}): Promise<TrackerRowBundle> {
  if (!opts.live) {
    return { rows: [], serviceRequests: [], lookup: buildTrackerRowLookup({}) };
  }
  const serviceRequests = await listServiceRequestsByStage(opts);

  const patientIds = new Set<string>();
  const episodeIds = new Set<string>();
  for (const sr of serviceRequests) {
    const pid = sr.subject?.reference?.replace('Patient/', '');
    if (pid) patientIds.add(pid);
    const eid = readEpisodeOfCareId(sr);
    if (eid) episodeIds.add(eid);
  }

  const [patients, episodes] = await Promise.all([
    patientIds.size > 0
      ? opts.medplum
          .searchResources('Patient', {
            _id: [...patientIds].join(','),
            _count: String(patientIds.size),
          })
          .catch(() => [] as Patient[])
      : Promise.resolve([] as Patient[]),
    episodeIds.size > 0
      ? opts.medplum
          .searchResources('EpisodeOfCare', {
            _id: [...episodeIds].join(','),
            _count: String(episodeIds.size),
          })
          .catch(() => [] as EpisodeOfCare[])
      : Promise.resolve([] as EpisodeOfCare[]),
  ]);

  const orgIds = new Set<string>();
  const practitionerIds = new Set<string>();
  for (const e of episodes) {
    const orgRef = e.managingOrganization?.reference;
    if (orgRef?.startsWith('Organization/')) {
      orgIds.add(orgRef.slice('Organization/'.length));
    }
    const pmRef = e.careManager?.reference;
    if (pmRef?.startsWith('Practitioner/')) {
      practitionerIds.add(pmRef.slice('Practitioner/'.length));
    }
  }

  const [organizations, practitioners, practitionerRoles, coverages, eligibilityResponses] = await Promise.all([
    orgIds.size > 0
      ? opts.medplum
          .searchResources('Organization', {
            _id: [...orgIds].join(','),
            _count: String(orgIds.size),
          })
          .catch(() => [])
      : Promise.resolve([]),
    practitionerIds.size > 0
      ? opts.medplum
          .searchResources('Practitioner', {
            _id: [...practitionerIds].join(','),
            _count: String(practitionerIds.size),
          })
          .catch(() => [])
      : Promise.resolve([]),
    practitionerIds.size > 0
      ? opts.medplum
          .searchResources('PractitionerRole', {
            practitioner: [...practitionerIds].map((id) => `Practitioner/${id}`).join(','),
            _count: '200',
          })
          .catch(() => [])
      : Promise.resolve([]),
    patientIds.size > 0
      ? opts.medplum
          .searchResources('Coverage', {
            beneficiary: [...patientIds].map((id) => `Patient/${id}`).join(','),
            _count: '200',
          })
          .catch(() => [] as Coverage[])
      : Promise.resolve([] as Coverage[]),
    patientIds.size > 0
      ? opts.medplum
          .searchResources('CoverageEligibilityResponse', {
            patient: [...patientIds].map((id) => `Patient/${id}`).join(','),
            status: 'active',
            _sort: '-created',
            _count: '200',
          })
          .catch(() => [] as CoverageEligibilityResponse[])
      : Promise.resolve([] as CoverageEligibilityResponse[]),
  ]);

  // Fetch transfer QRs for SRs in transfer/consultation stages
  const transferEpisodeIds = new Set<string>();
  const transferEpisodePatientIds = new Map<string, string>();
  for (const sr of serviceRequests) {
    const stage = readTrackerStage(sr.meta);
    if (stage !== 'transfer' && stage !== 'consultation') continue;
    const eid = readEpisodeOfCareId(sr);
    const pid = sr.subject?.reference?.replace(/^Patient\//, '');
    if (eid) transferEpisodeIds.add(eid);
    if (eid && pid) transferEpisodePatientIds.set(eid, pid);
  }

  const transferQrsByEpisodeId = new Map<string, QuestionnaireResponse>();
  if (transferEpisodeIds.size > 0) {
    const { getTransferQRForEpisode } = await import('./transferService');
    const qrResults = await Promise.all(
      [...transferEpisodeIds].map(async (eid) => {
        const qr = await getTransferQRForEpisode(opts.medplum, eid, {
          patientId: transferEpisodePatientIds.get(eid),
        }).catch(() => undefined);
        return [eid, qr] as const;
      }),
    );
    for (const [eid, qr] of qrResults) {
      if (qr) transferQrsByEpisodeId.set(eid, qr);
    }
  }

  // Fetch booked screening-call appointments for screening-stage patients so the tracker can show
  // the Appointment Date + Screening Provider and disable re-booking.
  const screeningPatientIds = new Set<string>();
  for (const sr of serviceRequests) {
    if (readTrackerStage(sr.meta) !== 'screening') continue;
    const pid = sr.subject?.reference?.replace(/^Patient\//, '');
    if (pid) screeningPatientIds.add(pid);
  }

  const screeningCallByPatientId = new Map<string, { start?: string; providerName?: string }>();
  if (screeningPatientIds.size > 0) {
    const appts = await opts.medplum
      .searchResources('Appointment', {
        actor: [...screeningPatientIds].map((id) => `Patient/${id}`).join(','),
        _sort: '-date',
        _count: '200',
      })
      .catch(() => [] as Appointment[]);

    // First (most recent) non-cancelled screening call per patient; collect its provider id.
    const callByPatient = new Map<string, Appointment>();
    const callPractitionerIds = new Set<string>();
    for (const appt of appts) {
      if (appt.status === 'cancelled' || !isScreeningCallAppointment(appt)) continue;
      const pid = appt.participant
        ?.find((p) => p.actor?.reference?.startsWith('Patient/'))
        ?.actor?.reference?.slice('Patient/'.length);
      if (!pid || callByPatient.has(pid)) continue;
      callByPatient.set(pid, appt);
      const prId = appointmentPractitionerId(appt);
      if (prId) callPractitionerIds.add(prId);
    }

    const callPractitioners = callPractitionerIds.size > 0
      ? await opts.medplum
          .searchResources('Practitioner', {
            _id: [...callPractitionerIds].join(','),
            _count: String(callPractitionerIds.size),
          })
          .catch(() => [] as Practitioner[])
      : [];
    const practitionerNameById = new Map<string, string>();
    for (const pr of callPractitioners) {
      if (pr.id) practitionerNameById.set(pr.id, practitionerFullName(pr) ?? pr.id);
    }

    for (const [pid, appt] of callByPatient) {
      const prId = appointmentPractitionerId(appt);
      screeningCallByPatientId.set(pid, {
        start: appt.start,
        providerName: prId ? practitionerNameById.get(prId) : undefined,
      });
    }
  }

  const lookup = buildTrackerRowLookup({
    patients,
    episodes,
    organizations,
    practitioners,
    practitionerRoles,
    coverages,
    eligibilityResponses,
  });
  lookup.transferQrsByEpisodeId = transferQrsByEpisodeId;
  lookup.screeningCallByPatientId = screeningCallByPatientId;

  const rows = serviceRequests.map((sr) => serviceRequestToTrackerRow(sr, lookup));
  return { rows, serviceRequests, lookup };
}

// ---------------------------------------------------------------------------
// Update (stage transitions + linked EpisodeOfCare side effects)
// ---------------------------------------------------------------------------

/** Matches `Annotation.authorReference` when present. */
type AnnotationAuthorReference = NonNullable<Annotation['authorReference']>;

export interface StageTransitionOptions {
  /**
   * When transitioning into `fulfilled`, set the EoC `careManager` to this practitioner.
   * Requires `careManagerReference` to be provided unless the EoC already has one.
   */
  careManagerReference?: EpisodeOfCareCareManagerReference;
  /** Optional note to append (admin/provider reason or outcome). */
  note?: string;
  /** Author for the appended note (Practitioner/Patient/Organization reference). */
  noteAuthor?: AnnotationAuthorReference;
}

/**
 * Transition a ServiceRequest to a new tracker stage and apply side effects to
 * the linked `EpisodeOfCare` as defined by the PRD.
 *
 * Side effect table (PRD):
 *  - → fulfilled: EoC.status = active; set careManager if provided.
 *  - → cancelled: EoC.status = cancelled.
 *  - → inactive:  EoC.status = onhold.
 *  - inactive → matching (reactivated): EoC.status = planned.
 *  - all other transitions: EoC remains planned.
 */
export async function transitionServiceRequestStage(opts: {
  live: boolean;
  medplum: MedplumClient;
  serviceRequest: ServiceRequest;
  nextStage: TrackerStageCode;
  options?: StageTransitionOptions;
}): Promise<ServiceRequestPair | null> {
  if (!opts.live) return null;
  const { medplum, serviceRequest, nextStage, options } = opts;

  const previousStage = readTrackerStage(serviceRequest.meta);
  const updatedSr: ServiceRequest = {
    ...serviceRequest,
    meta: upsertTrackerStageTag(serviceRequest.meta, nextStage),
  };
  if (options?.note) {
    const annotation: Annotation = {
      text: options.note,
      time: nowIso(),
      ...(options.noteAuthor ? { authorReference: options.noteAuthor } : {}),
    };
    updatedSr.note = [...(serviceRequest.note ?? []), annotation];
  }

  const savedSr = await medplum.updateResource<ServiceRequest>(updatedSr);

  const eocId = readEpisodeOfCareId(savedSr);
  let savedEoc: EpisodeOfCare | undefined;
  if (eocId) {
    const eoc = await medplum.readResource('EpisodeOfCare', eocId);
    const nextEocStatus = deriveEpisodeStatus(
      previousStage,
      nextStage,
      eoc.status,
    );
    const patch: EpisodeOfCare = {
      ...eoc,
      status: nextEocStatus,
    };

    // Fulfillment: set careManager + extend period.start if missing.
    if (nextStage === 'fulfilled') {
      if (options?.careManagerReference) {
        patch.careManager = options.careManagerReference;
      }
      // An active episode without a careManager is rejected by the
      // UnifyEpisodeOfCare profile, so fail here rather than on the write.
      if (!patch.careManager) {
        throw new Error(
          'Cannot fulfill this service request without a care manager. Assign a provider and try again.',
        );
      }
      if (!eoc.period?.start) {
        patch.period = { ...(eoc.period ?? {}), start: nowIso() };
      }
    }
    if (nextStage === 'cancelled' && !eoc.period?.end) {
      patch.period = { ...(eoc.period ?? {}), end: nowIso() };
    }
    if (nextStage === 'inactive' && !eoc.period?.end) {
      // Keep existing start; record pause timestamp via period.end placeholder is not in FHIR;
      // onhold status is the authoritative signal. No period.end changes.
    }

    savedEoc = await medplum.updateResource<EpisodeOfCare>(patch);

    // TODO(care-team): on fulfillment, run `$set-accounts` and ensure CareTeam membership
    // once that workflow lives in medplum-ui. Safe no-op today — the caseload query still
    // reads EpisodeOfCare.careManager.
  }

  return {
    serviceRequest: savedSr,
    episodeOfCare: savedEoc ?? buildEpisodeOfCareResource({
      patient: {
        resourceType: 'Patient',
        id: savedSr.subject?.reference?.replace('Patient/', '') ?? 'unknown',
      } as Patient,
      therapyTypeCode: 'individual',
    }),
  };
}

function deriveEpisodeStatus(
  previousStage: TrackerStageCode | undefined,
  nextStage: TrackerStageCode,
  currentEocStatus: EpisodeOfCare['status'],
): EpisodeOfCare['status'] {
  const { episodeOfCareStatus } = applyStageToStatus(nextStage);
  if (previousStage === 'inactive' && nextStage === 'matching') {
    return 'planned';
  }
  // Transfer: outgoing provider continues care, so EoC stays active.
  if (previousStage === 'fulfilled' && nextStage === 'transfer') {
    return currentEocStatus === 'active' ? 'active' : currentEocStatus;
  }
  // Bad-fit fallback returning to transfer also preserves active EoC.
  if (previousStage === 'consultation' && nextStage === 'transfer') {
    return currentEocStatus === 'active' ? 'active' : currentEocStatus;
  }
  // If moving back *from* fulfilled to an earlier stage (admin correction), don't
  // clobber an `active` EoC blindly — prefer to follow the canonical mapping.
  if (currentEocStatus === 'finished' && nextStage !== 'fulfilled') {
    return currentEocStatus;
  }
  return episodeOfCareStatus;
}

// ---------------------------------------------------------------------------
// Convenience: create one ServiceRequest+EoC per selected therapy type (intake)
// ---------------------------------------------------------------------------

export interface CreateIntakeServiceRequestsInput {
  patient: Patient;
  therapyTypeCodes: string[];
  /** Who originated the request (typically the patient for self-intake). */
  requester?: ServiceRequestRequesterReference;
  initialNote?: string;
  managingOrganization?: EpisodeOfCareManagingOrganizationReference;
  /** Stage to record. Defaults to `new-client` for public intake. */
  stage?: TrackerStageCode;
}

/**
 * For each selected therapy type at intake, create one ServiceRequest + one paired
 * EpisodeOfCare. Per PRD section "Public Intake Flow Integration".
 */
export async function createIntakeServiceRequests(opts: {
  live: boolean;
  medplum: MedplumClient;
  input: CreateIntakeServiceRequestsInput;
}): Promise<ServiceRequestPair[]> {
  if (!opts.live) return [];
  const { medplum, input } = opts;
  const results: ServiceRequestPair[] = [];
  for (const code of input.therapyTypeCodes) {
    const pair = await createServiceRequestPair({
      live: true,
      medplum,
      input: {
        patient: input.patient,
        therapyTypeCode: code,
        stage: input.stage ?? 'new-client',
        requester: input.requester,
        managingOrganization: input.managingOrganization,
        initialNote: input.initialNote,
      },
    });
    if (pair) results.push(pair);
  }
  return results;
}

// ---------------------------------------------------------------------------
// Utilities re-exported for consumers that only want the helpers
// ---------------------------------------------------------------------------

export { TRACKER_STAGE_TAG_SYSTEM };

/** Return a lookup-ready `TrackerRowLookup` from previously fetched practitioners. */
export function practitionerNamesToLookup(
  practitioners: Practitioner[],
): TrackerRowLookup {
  return buildTrackerRowLookup({ practitioners });
}
